'use client';

import {useEffect, type ReactNode} from 'react';
import {cx} from '@/lib/utils';
import {useI18n} from '@/lib/i18n/client';

interface ModalProps {
    open: boolean;
    onClose: () => void;
    /** id of the heading element inside `children`, wired to aria-labelledby */
    titleId: string;
    /** Default true. Pass false for non-explicit-dismiss consumers like IdleNotification. */
    showCloseButton?: boolean;
    /** Default 50. Stacked modals (e.g. LegalDocModal) bump this to 60. */
    zIndex?: number;
    /** Override or extend the card's Tailwind classes. Useful for width overrides. */
    cardClassName?: string;
    children: ReactNode;
}

export default function Modal({
    open,
    onClose,
    titleId,
    showCloseButton = true,
    zIndex = 50,
    cardClassName,
    children,
}: ModalProps) {
    let {t} = useI18n();

    useEffect(() => {
        if (!open) return;
        ensureGlobalListener();
        let pop = pushEscHandler(onClose);
        return pop;
    }, [open, onClose]);

    if (!open) return null;

    function handleBackdropClick() {
        onClose();
    }

    return (
        <div
            className="fixed inset-0 flex items-center justify-center"
            style={{zIndex}}
        >
            <div
                className="absolute inset-0 animate-blur-in"
                onClick={handleBackdropClick}
            />

            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                className={cx(
                    'relative z-10 bg-phaethon-bg rounded-pill shadow-[0px_4px_34.5px_3px_rgba(131,131,131,0.25)] max-w-[75%] lg:max-w-[60%] xl:max-w-[50%] 2xl:max-w-[40%] max-h-[85vh] animate-fade-in',
                    cardClassName,
                )}
            >
                {showCloseButton && (
                    <button
                        type="button"
                        aria-label={t('common.close')}
                        onClick={onClose}
                        className="absolute top-4 right-6 z-10 text-phaethon-text text-heading leading-none! transition-opacity hover:opacity-80"
                    >
                        ×
                    </button>
                )}
                <div className="max-h-[85vh] overflow-y-auto rounded-pill px-20 py-14 lg:px-24 lg:py-16 xl:px-26 xl:py-17 2xl:px-28 2xl:py-18">
                    {children}
                </div>
            </div>
        </div>
    );
}

// Topmost-only Esc registry: a stacked LegalDocModal opened over ConsentModal
// must consume Esc first so dismissing the doc doesn't also dismiss consent.
let escStack: Array<() => void> = [];

function pushEscHandler(handler: () => void) {
    escStack.push(handler);
    return function pop() {
        escStack = escStack.filter(h => h !== handler);
    };
}

function handleGlobalKeyDown(event: KeyboardEvent) {
    if (event.key !== 'Escape') return;
    let topmost = escStack[escStack.length - 1];
    if (topmost) {
        event.stopPropagation();
        topmost();
    }
}

let globalListenerInstalled = false;
function ensureGlobalListener() {
    if (globalListenerInstalled) return;
    if (typeof window === 'undefined') return;
    window.addEventListener('keydown', handleGlobalKeyDown);
    globalListenerInstalled = true;
}
